import { Icon } from '@buggy/shared'
import { Button, Flex, Rate } from 'antd'
import { toNumber } from 'lodash-es'
import type { FunctionComponent, ReactNode } from 'react'
import styled, {
  css,
  useTheme,
  type DefaultTheme,
  type FlattenInterpolation,
  type ThemeProps,
} from 'styled-components'
import DeleteButton from '@/components/common/buttons/delete-button'
import HoverableElement from '@/components/common/hoverable-element'
import PermissionInsufficientTooltip from '@/components/common/permission-insufficient-tooltip'
import { AvatarImage } from '@/components/workspace/avatar-image'
import { useAuth } from '@/hooks/use-auth'
import { useDirectoryActionPermission } from '@/hooks/use-permission'
import { useIntl } from '@/i18n/hooks/use-intl'
import type { Scoring } from '@/types/schema/directory/collaboration-record'

export interface ScoringViewProps {
  style?: 'default' | 'legacy'
  scoring: Scoring
  onClickEditScoring?: (scoring: Scoring) => void
  onDeleteScoring: (scoring: Scoring) => Promise<void>
}

const ScoringView: FunctionComponent<ScoringViewProps> = ({
  style = 'default',
  scoring,
  onClickEditScoring,
  onDeleteScoring,
}) => {
  const { formatDate, formatMessage } = useIntl()
  const theme = useTheme()

  const { userInfo } = useAuth()
  const isCreator = scoring.user_id === toNumber(userInfo?.id)
  const directoryActionPermission = useDirectoryActionPermission()
  const isLegacy = style === 'legacy'

  return (
    <Container isLegacy={isLegacy}>
      {(hovered): ReactNode => (
        <>
          {isLegacy && <AvatarImage name={scoring.user} />}
          <Flex vertical flex={1}>
            <Flex justify='space-between' style={{ minHeight: 32 }}>
              <Flex align='center' gap={8}>
                {!isLegacy && <AvatarImage name={scoring.user} size='small' />}
                <ManagerName>{scoring.user}</ManagerName>
                <StartDate>
                  {formatDate(scoring.evaluation_time, 'dateTimeLong')}
                </StartDate>
              </Flex>
              {hovered && (
                <Flex>
                  {isCreator && onClickEditScoring && (
                    <PermissionInsufficientTooltip
                      insufficient={!directoryActionPermission}
                    >
                      <Button
                        disabled={!directoryActionPermission}
                        icon={
                          <Icon
                            color={theme.colors.brand.primary}
                            fontSize={16}
                            name='edit-3'
                          />
                        }
                        size='small'
                        type='text'
                        onClick={(event): void => {
                          event.stopPropagation()
                          onClickEditScoring(scoring)
                        }}
                      />
                    </PermissionInsufficientTooltip>
                  )}
                  <PermissionInsufficientTooltip
                    insufficient={!directoryActionPermission}
                  >
                    <DeleteButton
                      confirmModalContent={formatMessage({
                        id: 'irm:deleted_cooperation_comment_modal_content',
                      })}
                      confirmModalTitle={formatMessage({
                        id: 'irm:update_deleted_cooperation_comment',
                      })}
                      disabled={!directoryActionPermission}
                      iconColor={theme.colors.brand.primary}
                      iconSize={16}
                      size='small'
                      type='text'
                      onDelete={async (): Promise<void> =>
                        await onDeleteScoring(scoring)
                      }
                    />
                  </PermissionInsufficientTooltip>
                </Flex>
              )}
            </Flex>
            <Rate allowHalf disabled value={scoring.evaluation_score} />
            <EvaluationContent>{scoring.evaluation_content}</EvaluationContent>
          </Flex>
        </>
      )}
    </Container>
  )
}

const Container = styled(HoverableElement)<{ isLegacy: boolean }>`
  background: ${({ theme }): string => theme.colors.background.white};

  .ant-rate {
    margin-bottom: 8px;
  }

  ${({ isLegacy }): FlattenInterpolation<ThemeProps<DefaultTheme>> =>
    isLegacy
      ? css`
          display: flex;
          padding: 0 0 16px;
          gap: 12px;
          border-bottom: 1px solid
            ${({ theme }): string => theme.colors.border.border};
        `
      : css`
          padding: 12px;
          border-radius: 4px;
          border: 1px solid ${({ theme }): string => theme.colors.border.border};
        `}
`

const StartDate = styled.div`
  color: ${({ theme }): string => theme.colors.text.tooltip};
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
`

const ManagerName = styled.div`
  color: ${({ theme }): string => theme.colors.text.secondary};
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
`

const EvaluationContent = styled.div`
  color: ${({ theme }): string => theme.colors.text.primary};
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
`

export default ScoringView
